import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeft, Plus, Lightbulb, Plug, Trash2, Zap, AlertCircle, Power, PowerOff
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getRooms, getDevices, createDevice, deleteDevice, controlRelay, updateDevice, getEspDevices } from '@/lib/api';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';
import usePolling from '@/hooks/usePolling';

const DeviceCard = ({ device, onToggle, onDelete }) => {
  const [toggling, setToggling] = useState(false);
  const isLight = device.type === 'light';
  const Icon = isLight ? Lightbulb : Plug;

  const handleToggle = async (checked) => {
    setToggling(true);
    await onToggle(device, checked);
    setToggling(false);
  };

  return (
    <div
      className={`bg-card border rounded-xl p-4 transition-all duration-300 ${
        device.state
          ? isLight ? 'device-on-light' : 'device-on-socket'
          : 'border-border'
      }`}
      data-testid={`device-card-${device.id}`}
    >
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-300 ${
          device.state
            ? isLight ? 'bg-yellow-400/20' : 'bg-green-400/20'
            : 'bg-muted'
        }`}>
          <Icon size={20} className={device.state ? (isLight ? 'text-yellow-500' : 'text-green-500') : 'text-muted-foreground'} />
        </div>
        <ConfirmDeleteDialog
          testId={`delete-device-${device.id}`}
          title="Cihazı Sil"
          description={`"${device.name}" cihazını silmek istediğinizden emin misiniz?`}
          onConfirm={() => onDelete(device.id)}
          trigger={
            <button className="text-muted-foreground hover:text-destructive transition-colors p-1" data-testid={`delete-device-${device.id}`}>
              <Trash2 size={14} />
            </button>
          }
        />
      </div>
      <div className="mt-3">
        <p className="font-semibold text-sm">{device.name}</p>
        <p className="text-xs text-muted-foreground capitalize mt-0.5">{isLight ? 'Lamba' : 'Priz'}</p>
      </div>
      <div className="flex items-center justify-between mt-3">
        {device.relay_number ? (
          <Badge variant="outline" className="text-xs font-mono-tech gap-1 max-w-full truncate">
            <Zap size={10} />
            {device.esp_device_id && device.esp_device_id !== 'global'
              ? `R${device.relay_number}`
              : `R${device.relay_number}`}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-xs text-muted-foreground">Röle yok</Badge>
        )}
        <Switch
          checked={device.state}
          onCheckedChange={handleToggle}
          disabled={toggling}
          className={`data-[state=checked]:${isLight ? 'bg-yellow-500' : 'bg-green-500'}`}
          data-testid={`device-toggle-${device.id}`}
        />
      </div>
    </div>
  );
};

const AddDeviceDialog = ({ roomId, room, espDevices, existingDevices, onAdded }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState('light');
  const [selectedRelay, setSelectedRelay] = useState(null); // {esp_device_id, relay_number}
  const [saving, setSaving] = useState(false);

  // Relay'leri odanın atamalarından al
  const hasAssignments = room?.relay_assignments?.some(a => a.relay_numbers?.length > 0);

  // Zaten kullanılan (başka cihaza atanmış) röle key'leri
  const usedRelayKeys = new Set(
    existingDevices
      .filter(d => d.relay_number)
      .map(d => `${d.esp_device_id || 'global'}_${d.relay_number}`)
  );

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Cihaz adı giriniz'); return; }
    setSaving(true);
    try {
      const res = await createDevice({
        room_id: roomId,
        name: name.trim(),
        type,
        relay_number: selectedRelay?.relay_number || null,
        esp_device_id: selectedRelay?.esp_device_id || 'global',
      });
      onAdded(res.data);
      setOpen(false);
      setName(''); setType('light'); setSelectedRelay(null);
      toast.success('Cihaz eklendi');
    } catch {
      toast.error('Cihaz eklenemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleOpen = (v) => {
    if (!v) { setName(''); setType('light'); setSelectedRelay(null); }
    setOpen(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="add-device-btn">
          <Plus size={16} /> Cihaz Ekle
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="add-device-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Yeni Cihaz Ekle</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
          {/* Cihaz Adı */}
          <div className="space-y-1.5">
            <Label>Cihaz Adı *</Label>
            <Input placeholder="Örn: Salon Lambası" value={name}
              onChange={(e) => setName(e.target.value)} data-testid="device-name-input" />
          </div>

          {/* Cihaz Türü */}
          <div className="space-y-1.5">
            <Label>Cihaz Türü</Label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { val: 'light', icon: Lightbulb, label: 'Lamba' },
                { val: 'socket', icon: Plug, label: 'Priz' },
              ].map(({ val, icon: Icon, label }) => (
                <button key={val} type="button" onClick={() => setType(val)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${type === val ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/30'}`}
                  data-testid={`device-type-${val}`}>
                  <Icon size={16} /><span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Röle Seçimi */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Zap size={13} className="text-primary" />
              Röle Seç
              {selectedRelay && (
                <Badge className="ml-1 text-xs bg-primary/10 text-primary border-primary/30">
                  {espDevices.find(d => d.id === selectedRelay.esp_device_id)?.name || 'ESP'} — R{selectedRelay.relay_number}
                </Badge>
              )}
            </Label>

            {!hasAssignments ? (
              <p className="text-xs text-muted-foreground bg-muted rounded-lg p-3">
                Bu odaya henüz röle atanmamış. Odayı düzenleyerek ESP cihazı ve röle atayabilirsiniz.
              </p>
            ) : (
              <div className="space-y-3 bg-muted/30 rounded-xl p-3 border border-border">
                {(room.relay_assignments || []).filter(a => a.relay_numbers?.length > 0).map(assignment => {
                  const dev = espDevices.find(d => d.id === assignment.esp_device_id);
                  const availableNums = assignment.relay_numbers.filter(
                    n => !usedRelayKeys.has(`${assignment.esp_device_id}_${n}`)
                  );

                  if (availableNums.length === 0) {
                    return (
                      <div key={assignment.esp_device_id}>
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`w-1.5 h-1.5 rounded-full ${dev?.online ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
                          <span className="text-xs font-semibold">{dev?.name || 'ESP Cihazı'}</span>
                          {dev?.zone && <Badge variant="outline" className="text-[10px]">{dev.zone}</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground italic">Tüm röleler kullanımda</p>
                      </div>
                    );
                  }

                  return (
                    <div key={assignment.esp_device_id}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-1.5 h-1.5 rounded-full ${dev?.online ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
                        <span className="text-xs font-semibold">{dev?.name || 'ESP Cihazı'}</span>
                        {dev?.zone && <Badge variant="outline" className="text-[10px]">{dev.zone}</Badge>}
                        <span className="text-[10px] text-muted-foreground ml-auto">{availableNums.length} müsait</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {availableNums.map(n => {
                          const isSelected = selectedRelay?.esp_device_id === assignment.esp_device_id && selectedRelay?.relay_number === n;
                          return (
                            <button key={n} type="button"
                              onClick={() => setSelectedRelay(
                                isSelected ? null : { esp_device_id: assignment.esp_device_id, relay_number: n }
                              )}
                              className={`h-9 w-9 rounded-xl text-xs font-bold font-mono-tech transition-all border ${
                                isSelected
                                  ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                                  : 'bg-card text-muted-foreground border-border hover:border-primary/50 hover:text-primary'
                              }`}
                              data-testid={`relay-pick-${assignment.esp_device_id}-${n}`}
                            >
                              {n}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {!selectedRelay && (
                  <p className="text-xs text-muted-foreground text-center pt-1">Bir röle seçin (opsiyonel)</p>
                )}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpen(false)}>İptal</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-device-btn">
            {saving ? 'Ekleniyor...' : 'Ekle'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RoomDetail = () => {
  const { id } = useParams();
  const [room, setRoom] = useState(null);
  const [devices, setDevices] = useState([]);
  const [espDevices, setEspDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDevices = useCallback(async () => {
    try {
      const res = await getDevices(id);
      setDevices(res.data);
    } catch { /* silent */ }
  }, [id]);

  useEffect(() => {
    const init = async () => {
      try {
        const [roomsRes, devicesRes, espRes] = await Promise.all([getRooms(), getDevices(id), getEspDevices()]);
        const found = roomsRes.data.find(r => r.id === id);
        setRoom(found);
        setDevices(devicesRes.data);
        setEspDevices(espRes.data);
      } catch {
        toast.error('Veriler yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id]);

  usePolling(loadDevices, 20000);

  const handleToggle = async (device, newState) => {
    setDevices(prev => prev.map(d => d.id === device.id ? { ...d, state: newState } : d));
    try {
      if (device.relay_number) {
        // esp_device_id'yi cihazdan al (yoksa 'global')
        await controlRelay(device.relay_number, newState, device.esp_device_id || 'global');
      } else {
        await updateDevice(device.id, { state: newState });
      }
    } catch {
      setDevices(prev => prev.map(d => d.id === device.id ? { ...d, state: !newState } : d));
      toast.error('Cihaz durumu güncellenemedi');
    }
  };

  const handleDelete = async (deviceId) => {
    try {
      await deleteDevice(deviceId);
      setDevices(prev => prev.filter(d => d.id !== deviceId));
      toast.success('Cihaz silindi');
    } catch {
      toast.error('Cihaz silinemedi');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-36 rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="text-center py-16">
        <AlertCircle size={40} className="mx-auto mb-3 text-muted-foreground opacity-40" />
        <p className="text-muted-foreground">Oda bulunamadı</p>
        <Link to="/rooms"><Button className="mt-4" variant="outline">Odalara Dön</Button></Link>
      </div>
    );
  }

  const activeCount = devices.filter(d => d.state).length;

  const handleBulkToggle = async (state) => {
    // Optimistik güncelleme
    setDevices(prev => prev.map(d => ({ ...d, state })));
    try {
      await Promise.all(devices.map(d =>
        d.relay_number
          ? controlRelay(d.relay_number, state, d.esp_device_id || 'global')
          : updateDevice(d.id, { state })
      ));
      toast.success(`Tüm cihazlar ${state ? 'açıldı' : 'kapatıldı'}`);
    } catch {
      setDevices(prev => prev.map(d => ({ ...d, state: !state })));
      toast.error('Toplu kontrol başarısız');
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/rooms">
          <Button variant="ghost" size="icon" className="rounded-xl" data-testid="back-btn">
            <ChevronLeft size={20} />
          </Button>
        </Link>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold" style={{fontFamily:'Outfit,sans-serif'}}>{room.name}</h1>
            {activeCount > 0 && (
              <Badge className="bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30">
                {activeCount} Aktif
              </Badge>
            )}
          </div>
          {room.description && <p className="text-muted-foreground text-sm mt-0.5">{room.description}</p>}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {devices.length > 0 && (
            <div className="flex items-center gap-1 p-1 bg-muted rounded-xl">
              <Button size="sm" variant="ghost"
                onClick={() => handleBulkToggle(true)}
                className="h-7 text-xs gap-1.5 hover:bg-green-500/15 hover:text-green-600 dark:hover:text-green-400"
                data-testid="bulk-on-btn">
                <Power size={12} /> Tümünü Aç
              </Button>
              <div className="w-px h-4 bg-border" />
              <Button size="sm" variant="ghost"
                onClick={() => handleBulkToggle(false)}
                className="h-7 text-xs gap-1.5 hover:bg-destructive/10 hover:text-destructive"
                data-testid="bulk-off-btn">
                <PowerOff size={12} /> Tümünü Kapat
              </Button>
            </div>
          )}
          <AddDeviceDialog
            roomId={id}
            room={room}
            espDevices={espDevices}
            existingDevices={devices}
            onAdded={(d) => setDevices(prev => [...prev, d])}
          />
        </div>
      </div>

      {devices.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl" data-testid="empty-devices">
          <Lightbulb size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-muted-foreground font-medium">Bu odada henüz cihaz yok</p>
          <p className="text-sm text-muted-foreground mt-1">Yukarıdaki "Cihaz Ekle" butonuna tıklayın</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="devices-grid">
          {devices.map(device => (
            <DeviceCard
              key={device.id}
              device={device}
              onToggle={handleToggle}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default RoomDetail;
